"use client";

import * as React from "react";
import { ChevronDown } from "lucide-react";

import { CORE_COLORS, CORE_FONT } from "@/lib/theme/core";
import { DIAL_CODES } from "@/lib/validation/auth";

/**
 * Country dial-code selector for the phone fields.
 *
 * Replaces a plain `<button>+852 ▾</button>` that had no handler and no menu —
 * QA: "下拉框没有功能" and "電話應該要容許選取區號同埋輸入電話號碼".
 *
 * A native `<select>` rather than a Radix popover: it is one control, it gets
 * the platform picker on iOS/Android for free, it is keyboard- and
 * screen-reader-accessible with no extra work, and it keeps the auth bundle
 * small. The visible chevron is decorative; the real `<select>` sits on top at
 * `opacity-0` so the styling matches the adjacent inputs exactly.
 */
export function CountryCodeSelect({
  value,
  onChange,
  id = "dial-code",
  height = 48,
}: {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  /**
   * Match the phone input this sits beside. The auth forms use 48px inputs;
   * the account profile modal uses 44px, and this was hard-coded to 48 so the
   * two controls in that row were visibly different heights.
   */
  height?: number;
}) {
  return (
    <div className="relative shrink-0" style={{ width: 104, height }}>
      <select
        id={id}
        aria-label="選擇區號"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      >
        {DIAL_CODES.map((d) => (
          <option key={d.code} value={d.code}>
            {d.label}
          </option>
        ))}
      </select>
      <div
        aria-hidden="true"
        className="pointer-events-none flex h-full w-full items-center justify-between"
        style={{
          fontFamily: CORE_FONT.sans,
          fontSize: "15px",
          color: CORE_COLORS.textPrimary,
          lineHeight: "22px",
          // Horizontal padding only — the box is height-constrained by the
          // wrapper, so a fixed vertical padding would fight a shorter height.
          padding: "0 14px",
          border: `1px solid rgba(0,0,0,0.3)`,
          borderRadius: "3px",
          background: "#ffffff",
        }}
      >
        <span>{value}</span>
        <ChevronDown
          className="size-[16px]"
          color={CORE_COLORS.textSecondary}
          strokeWidth={2}
        />
      </div>
    </div>
  );
}
